import { Button, Group, Tooltip } from "@mantine/core";
import React, { useState } from "react";
import _ from "lodash";

import NewTicketButton from "./newTicketButton";
import { useServerApi } from "../hooks/userServerApi";
import DebitNoteConfirmPaymentButton from "./debitNoteConfirmPaymentButton";
import DebitNoteUnConfirmPaymentButton from "./debitNoteUnConfirmPaymentButton";
import DebitNoteSettlePaymentButton from "./debitNoteSettlePaymentButton";
import DebitNoteUnSettlePaymentButton from "./debitNoteUnSettlePaymentButton";
import DebitNoteEndorseButton from "./debitNoteEndorseButton";

import QuotationConfirmButton from "./quotationConfirmButton";
import { useDisclosure } from "@mantine/hooks";
import ReactJson from "react-json-view";
import DebitNoteEditButton from "./debitNoteEditButton";
import DebitNoteAdjustButton from "./debitNoteAdjustButton";
import DebitNoteHoldCoverButton from "./debitNoteHoldCoverButton";
import DebitNoteConfirmPolicyButton from "./debitNoteConfirmPolicyButton";

import { showNotification } from "@mantine/notifications";
import { IconAlertCircle } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import ActionPermissionGate from "./actionPermissionGate";
import { saveFormFields } from "../helpers/formFieldSave";
import { openFileUrl } from "../utils/openReport";

const STATUS_ALLOWED_TO_EDIT = ["DFT", "WAIT", "CAN"];

const STATUS_ALLOWED_TO_ADJUST = ["HOLD", "CONFIRMED", "SETTLED"];
const STATUS_ALLOWED_TO_PRINT_RECEIPT = ["PAID", "SETTLED", "EXP", "DIRECT"];
const STATUS_ALLOWED_TO_COVER = ["DFT", "WAIT", "CAN", "HOLD"];
const STATUS_ALLOWED_TO_TERMINATE = ["DFT", "WAIT", "CAN", "HOLD", "CONFIRMED"];
const PAYMENT_STATUS_ALLOWED_TO_CONFIRM = ["DFT", "WAIT"];
const PAYMENT_STATUS_ALLOWED_TO_SETTLE = ["PAID"];
const PAYMENT_STATUS_ALLOWED_TO_UNSETTLE = ["SETTLED"];
const PAYMENT_STATUS_ALLOWED_TO_PRINT_RECEIPT = process.env
  .REACT_APP_DEBIT_NOTE_ALLOW_PRINT_RECEIPT
  ? process.env.REACT_APP_DEBIT_NOTE_ALLOW_PRINT_RECEIPT?.split(",").map((s) =>
      s.trim()
    )
  : ["PAID", "SETTLED", "EXP", "DIRECT", "MATCHING", "PARTIAL"];

const PrintButton = ({
  mainForm,
  type = "pdf",
  label = "Print Debit Note",
}) => {
  const [api] = useServerApi();
  const [printing, setPrinting] = useState(false);
  const { t: translate } = useTranslation();

  const print = async () => {
    try {
      const { _id } = mainForm.values;
      if (!_id) return;

      setPrinting(true);

      let result = await api.DebitNote.print(_id, type);
      if (!result?.success || !result?.data?.url) {
        setPrinting(false);
        showNotification({
          title: translate("Print Fail"),
          color: "red",
          icon: <IconAlertCircle size={18} />,
          message:
            result?.message ||
            translate("Print did not return a download URL. Check server/nginx /download/ proxy."),
        });
        return;
      }

      const url = String(result.data.url);
      if (!url) {
        setPrinting(false);
        showNotification({
          title: translate("Print Fail"),
          color: "red",
          icon: <IconAlertCircle size={18} />,
          message: translate("Print did not return a download URL."),
        });
        return;
      }

      await openFileUrl(url);
      mainForm.setFieldValue("status", result.data.status);
      mainForm.setFieldValue("paymentStatus", result.data.paymentStatus);
      mainForm.notifyListRefresh?.();
      setPrinting(false);
    } catch (error) {
      console.log(error);
      setPrinting(false);
      showNotification({
        title: translate("Print Fail"),
        color: "red",
        icon: <IconAlertCircle size={18} />,
        message: String(error?.message || error),
      });
    }
  };

  const labelOutput = printing ? translate("Printing") : translate(label);

  return (
    <Button fullWidth onClick={() => print()} loading={printing}>
      {labelOutput}
    </Button>
  );
};

const PrintReceiptButton = ({ mainForm, disabled }) => {
  const [api] = useServerApi();
  const [printing, setPrinting] = useState(false);
  const { t } = useTranslation();
  const print = async () => {
    try {
      const { _id } = mainForm.values;
      if (!_id) return;

      setPrinting(true);

      let result = await api.DebitNote.printReceipt(_id);
      // console.log(result.data.url);
      if (!result || !result.data || !result.data.url) return;
      await openFileUrl(result.data.url);

      setPrinting(false);
    } catch (error) {
      console.log(error);
      setPrinting(false);
      showNotification({
        title: t("Print Receipt Fail"),
        icon: <IconAlertCircle size={18} />,
        color: "red",
        message: error,
      });
    }
  };

  return (
    <Button fullWidth onClick={() => print()} loading={printing} disabled={disabled}>
      {printing ? t("Printing") : t("Print Receipt")}
    </Button>
  );
};

const PrintRNButton = ({ mainForm }) => {
  const [api] = useServerApi();
  const [printing, setPrinting] = useState(false);
  const { t } = useTranslation();

  const print = async () => {
    try {
      const { _id } = mainForm.values;
      if (!_id) return;

      setPrinting(true);

      let result = await api.DebitNote.printRenewalNotice(_id);
      // console.log(result.data.url);
      if (!result || !result.data || !result.data.url) return;
      await openFileUrl(result.data.url);
      mainForm.setFieldValue("status", result.data.status);
      mainForm.notifyListRefresh?.();
      setPrinting(false);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Button fullWidth onClick={() => print()} loading={printing}>
      {printing ? t("Printing") : t("Print Renewal Notice")}
    </Button>
  );
};

const TerminateButton = ({ mainForm }) => {
  const { t } = useTranslation();
  const terminate = () => {
    saveFormFields(mainForm, { status: "CAN" });
  };

  return (
    <Button fullWidth color={"red"} onClick={() => terminate()}>
      {t("Terminate")}
    </Button>
  );
};

const DebitNoteActionList = ({ form }) => {
  const { t } = useTranslation();
  const {
    status,
    paymentStatus,
    code,
    consultants,
    _id,
    signedBy,
    costTotal,
    total,
    clientPayable,
    payment,
  } = form.values;

  const DEBITNOTE_STATUS = [
    "DFT", // Initial Draft
    "WAIT", //Waiting to confirm
    "CAN", //Cancel / reject by client or provider
    "HOLD", //Hold Cover by Provider (Temp Confirm)
    "CONFIRMED", //Policy Confirmed
    "SETTLED", //will remove (ToDo)
    "EXP", //Expired. Policy Finish
  ];
  const DEBITNOTE_PAYMENT_STATUS = [
    "DFT", // Draft - Initial payment state when debit note is created
    "WAIT", // Waiting - Awaiting payment from client
    "MATCHING", // Waiting Matching - Waiting for matching payment in accounts
    "PAID", // Paid - Confirmed by Client. Payment is confirmed
    "SETTLED", // Settled - Payment is settled to provider and marked by accountant
  ];

  const PAYMENT_STATUS_ALLOWED_TO_UNCONFIRM = ["MATCHING"];
  const PAYMENT_STATUS_TO_LOCK =
    process.env.REACT_APP_LOCK_DN_PAYMENT_STATUS?.split(",") ?? [];

  const showConfirmPayment =
    paymentStatus === "WAIT" || paymentStatus === "DFT";

  const showPrint = true;

  const allowToEdit =
    paymentStatus !== "SETTLED" &&
    !PAYMENT_STATUS_TO_LOCK.includes(paymentStatus);

  const allowToAdjust =
    STATUS_ALLOWED_TO_ADJUST.includes(status) &&
    !PAYMENT_STATUS_TO_LOCK.includes(paymentStatus);

  const allowToCover = STATUS_ALLOWED_TO_COVER.includes(status);
  const allowToConfirmPayment =
    PAYMENT_STATUS_ALLOWED_TO_CONFIRM.includes(paymentStatus);

  const allowToUnConfirmPayment =
    PAYMENT_STATUS_ALLOWED_TO_UNCONFIRM.includes(paymentStatus);

  const allowToSettlePayment =
    PAYMENT_STATUS_ALLOWED_TO_SETTLE.includes(paymentStatus);
  const allowToUnSettlePayment =
    PAYMENT_STATUS_ALLOWED_TO_UNSETTLE.includes(paymentStatus);

  const allowToPrintReceipt =
    PAYMENT_STATUS_ALLOWED_TO_PRINT_RECEIPT.includes(paymentStatus);
  // ||
  // (clientPayable > 0 &&
  //   payment?.amount > 0 &&
  //   _.round(payment?.amount - clientPayable, 2) >= -0.0001);

  const allowToTerminate = STATUS_ALLOWED_TO_TERMINATE.includes(status);

  const showSettle = true; //status === "PAID";
  const showUnSettle = true; //paymentStatus === "SETTLED";

  return (
    <>
      {/* role:{role} */}
      {/* <ReactJson
        src={PAYMENT_STATUS_ALLOWED_TO_PRINT_RECEIPT}
        style={{ background: "white" }}
      /> */}
      <Group mt={10}>
        <ActionPermissionGate
          entity="debitNote"
          action="printPdf"
          requireFullAccess={false}
        >
          <PrintButton mainForm={form} type="pdf" label="Print Debit Note" />
        </ActionPermissionGate>
        <ActionPermissionGate
          entity="debitNote"
          action="printExcel"
          requireFullAccess={false}
        >
          <PrintButton mainForm={form} type="excel" label="Download" />
        </ActionPermissionGate>
        <ActionPermissionGate
          entity="debitNote"
          action="printReceipt"
          requireFullAccess={false}
        >
          <Tooltip
            label={t("Available only after status is PAID or SETTLED")}
            disabled={allowToPrintReceipt}
          >
            <div style={{ display: "inline-block", width: "100%" }}>
              <PrintReceiptButton
                mainForm={form}
                disabled={!allowToPrintReceipt}
              />
            </div>
          </Tooltip>
        </ActionPermissionGate>
        <ActionPermissionGate entity="debitNote" action="editDebitNote">
          <DebitNoteEditButton mainForm={form} disabled={!allowToEdit} />
        </ActionPermissionGate>
        <ActionPermissionGate entity="debitNote" action="adjustDebitNote">
          <DebitNoteAdjustButton mainForm={form} disabled={!allowToAdjust} />
        </ActionPermissionGate>

        <ActionPermissionGate entity="debitNote" action="createTicket">
          <NewTicketButton
            buttonType="fullWidth"
            code={code}
            entityId={_id}
            entity="debitNote"
            ticketType={"TK-GI-DEBIT"}
            members={consultants?.map((c) => c._id)}
          />
        </ActionPermissionGate>

        <ActionPermissionGate entity="debitNote" action="confirmPolicy">
          <DebitNoteConfirmPolicyButton mainForm={form} />
        </ActionPermissionGate>

        {allowToUnSettlePayment && (
          <ActionPermissionGate entity="debitNote" action="unsettlePayment">
            <DebitNoteUnSettlePaymentButton mainForm={form} />
          </ActionPermissionGate>
        )}

        {allowToTerminate && (
          <ActionPermissionGate entity="debitNote" action="terminate">
            <TerminateButton mainForm={form} />
          </ActionPermissionGate>
        )}
      </Group>
    </>
  );
};

export default DebitNoteActionList;
